import type {WorkspaceProduct} from './OrderingCenterWorkspaces.tsx';
import type {SyncedComboPool,SyncedOptionSet} from '../../runtime/published-config-projection.ts';
export const DRINK_SUPPLEMENT_PRODUCT_PREFIX='drink-supplement:' as const;

export interface WorkspaceDrinkSupplementChoice{
  readonly id:string;
  readonly label:string;
  readonly adjustmentMinor:number;
  readonly kind:'PRODUCT'|'LABEL'|'NONE';
  readonly productId?:string;
  readonly enabled:boolean;
  readonly requiresConfiguration:boolean;
  readonly optionSets?:readonly SyncedOptionSet[];
}

export function isDrinkSupplementProductId(productId:string){
  return productId.startsWith(DRINK_SUPPLEMENT_PRODUCT_PREFIX);
}

export function projectDrinkSupplementChoices(
  products:readonly WorkspaceProduct[],
  pools:readonly SyncedComboPool[],
):WorkspaceDrinkSupplementChoice[]{
  const byProduct=new Map(products.map(product=>[product.id,product] as const));
  const rows:WorkspaceDrinkSupplementChoice[]=[];
  for(const pool of pools){
    if(pool.kind!=='ADDON'||pool.addonKind!=='DRINK')continue;
    for(const group of pool.groups){
      for(const subPool of group.subPools){
        for(const choice of subPool.choices){
          const product=choice.type==='PRODUCT'&&choice.productId?byProduct.get(choice.productId):undefined;
          rows.push(Object.freeze({
            id:[pool.id,group.id,subPool.id,choice.id].join('::'),
            label:choice.type==='PRODUCT'
              ?(product?.name??choice.label??choice.productId??'未命名飲品')
              :choice.label,
            adjustmentMinor:subPool.priceAdjustmentMinor+choice.priceAdjustmentMinor,
            kind:choice.type,
            ...(choice.productId?{productId:choice.productId}:{}),
            enabled:choice.type!=='PRODUCT'||Boolean(product),
            requiresConfiguration:Boolean(choice.type==='LABEL'?choice.optionSets?.length:product?.optionSets?.length),
            ...(choice.type==='LABEL'&&choice.optionSets?.length?{optionSets:choice.optionSets}:{}),
          }));
        }
      }
    }
  }
  return rows;
}


/** Configurator view only. LABEL identity stays on the existing meal DRINK row;
 * this view is never inserted into catalog, product pickers or standalone sales. */
export function drinkSupplementConfigurationView(choice:WorkspaceDrinkSupplementChoice|undefined,products:readonly WorkspaceProduct[]):WorkspaceProduct|undefined{
  if(!choice||!choice.enabled||choice.kind==='NONE')return undefined;
  if(choice.kind==='PRODUCT')return products.find(product=>product.id===choice.productId);
  if(!choice.optionSets?.length)return undefined;
  return {id:choice.id,name:choice.label,category:'跟餐飲品',priceMinor:choice.adjustmentMinor,priceLabel:'',optionSets:choice.optionSets};
}

/** Missing/foreign LABEL configuration blocks checkout just like a product's
 * required groups. No option is inferred from receipt text or selected silently. */
export function pendingLabelDrinkConfigurations(cart:readonly {id:string;optionSelections?:Readonly<Record<string,readonly string[]>>;meal?:{role:string;groupId:string;drinkChoiceId:string}}[],choices:readonly WorkspaceDrinkSupplementChoice[]){
  return cart.flatMap(line=>{
    if(line.meal?.role!=='DRINK')return [];
    const choice=choices.find(choice=>choice.id===line.meal!.drinkChoiceId);
    if(choice?.kind!=='LABEL'||!choice.optionSets?.length)return [];
    const selected=line.optionSelections??{},sets=choice.optionSets;
    const invalid=Object.keys(selected).some(id=>!sets.some(set=>set.id===id))||sets.some(set=>{
      const ids=selected[set.id]??[],min=Math.max(set.required?1:0,set.min);
      return ids.length<min||ids.length>Math.max(min,set.max)||new Set(ids).size!==ids.length||ids.some(id=>!set.options.some(option=>option.id===id&&option.active));
    });
    return invalid?[{lineId:line.id,choiceId:choice.id,targetId:line.meal.groupId}]:[];
  });
}
